import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { cn } from "@/lib/utils";
import { HOME_BOUNDS, layerById, loadMapConfig, applyCleanStyle, applySatelliteStyle } from "./mapLayers";
import { arc, layoutMarkers, clusterPoints } from "./mapUtils";

/**
 * OpsMap – the shared Mapbox GL surface for Oversikt, Kart and Flåte.
 *
 *  - vector basemap (light, de-cluttered) or satellite; style switches keep camera and re-emit `styleTick` so
 *    children that own GL layers (TransportLines) re-add them
 *  - children render inside the map context: <MapMarker/> (HTML markers through portals) and <TransportLines/>
 *  - `bounds` fits the view (animated after first paint); background clicks call `onBackgroundClick`
 */
const MapCtx = createContext(null);
export const useOpsMap = () => useContext(MapCtx);

export const OpsMap = ({ layer = "light", bounds, fitPadding = 56, maxZoom = 11, onBackgroundClick, interactive = true, className, children, testId = "ops-map", onReady }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(null);
  const [map, setMap] = useState(null);
  const [ready, setReady] = useState(false);
  const [styleTick, setStyleTick] = useState(0);
  const [zoom, setZoom] = useState(7);
  const [tick, setTick] = useState(0); // bumps on moveend → markers re-layout
  const firstFit = useRef(true);
  const layerRef = useRef(layer);
  const boundsKey = JSON.stringify(bounds || null);

  useEffect(() => {
    let alive = true;
    loadMapConfig().then((c) => alive && setConfig(c)).catch((e) => alive && setConfigError(e));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current || mapRef.current || !config?.token) return;
    mapboxgl.accessToken = config.token;
    const m = new mapboxgl.Map({
      container: containerRef.current,
      style: config.styles[layerRef.current] || config.styles.light,
      bounds: HOME_BOUNDS,
      fitBoundsOptions: { padding: fitPadding },
      attributionControl: false,
      logoPosition: "bottom-left",
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      interactive,
      maxZoom: 18,
      minZoom: 4,
      fadeDuration: 150,
      projection: "mercator",
    });
    m.touchZoomRotate?.disableRotation?.();
    const onStyle = () => {
      if (layerRef.current === "light") applyCleanStyle(m);
      else applySatelliteStyle(m);
      setReady(true);
      setStyleTick((t) => t + 1);
      onReady?.(m);
    };
    m.on("style.load", onStyle);
    m.on("error", (e) => {
      const status = e?.error?.status;
      if (status && status !== 401 && status !== 403) return; // tile misses outside coverage are harmless
      if (status) console.warn("Kart: ikke autorisert mot kartleverandør");
    });
    m.on("zoom", () => setZoom(m.getZoom()));
    m.on("moveend", () => {
      setTick((t) => t + 1);
      const c = m.getCenter();
      const root = containerRef.current?.parentElement;
      if (root) {
        root.dataset.mapZoom = m.getZoom().toFixed(2);
        root.dataset.mapCenter = `${c.lng.toFixed(3)},${c.lat.toFixed(3)}`;
      }
    });
    m.on("click", (e) => {
      if (e.originalEvent?.target?.closest?.(".ops-marker")) return;
      onBackgroundClick?.();
    });
    mapRef.current = m;
    setMap(m);
    setZoom(m.getZoom());
    return () => {
      m.remove();
      mapRef.current = null;
      setMap(null);
      setReady(false);
    };
     
  }, [config]);

  // basemap switch – keeps the camera, children re-add their layers via styleTick
  useEffect(() => {
    if (!map || !config) return;
    if (layerRef.current === layer) return;
    layerRef.current = layer;
    setReady(false);
    map.setStyle(config.styles[layer] || config.styles.light);
  }, [map, config, layer]);

  // fit to data (padding compared by value – callers may pass a fresh object every render)
  const padKey = JSON.stringify(fitPadding);
  useEffect(() => {
    if (!map || !bounds) return;
    map.fitBounds(bounds, { padding: fitPadding, maxZoom, duration: firstFit.current ? 0 : 700, essential: true });
    firstFit.current = false;
     
  }, [map, boundsKey, padKey, maxZoom]);

  // keep the canvas in sync with layout changes (grid reflow, sidebar collapse)
  useEffect(() => {
    if (!map || !containerRef.current) return;
    const ro = new ResizeObserver(() => map.resize());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [map]);

  const ctx = useMemo(() => ({ map, ready, styleTick, zoom, tick, layer: layerById(layer) }), [map, ready, styleTick, zoom, tick, layer]);

  return (
    <div className={cn("relative overflow-hidden bg-[#eef1f5] dark:bg-muted", className)} data-testid={testId} data-map-provider={config?.provider || "loading"} data-map-layer={layer}>
      <div ref={containerRef} className="h-full w-full [&_.mapboxgl-canvas]:outline-none [&_.mapboxgl-ctrl-logo]:opacity-60" />
      {!ready && !configError && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-testid="ops-map-loading">
          <div className="h-8 w-8 animate-pulse rounded-full bg-foreground/10" />
        </div>
      )}
      {(configError || (config && !config.token)) && (
        <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-[12.5px] text-muted-foreground" data-testid="ops-map-unavailable">
          Kartet er ikke tilgjengelig – kartleverandør er ikke konfigurert.
        </div>
      )}
      {map && <MapCtx.Provider value={ctx}>{children}</MapCtx.Provider>}
    </div>
  );
};

/** React children rendered as a Mapbox HTML marker. `offset` is in pixels (used for decluttering). */
export const MapMarker = ({ lat, lng, offset, anchor = "center", zIndex, children, className }) => {
  const { map } = useOpsMap();
  const el = useMemo(() => {
    const d = document.createElement("div");
    d.className = "ops-marker-root";
    return d;
  }, []);
  const markerRef = useRef(null);

  useLayoutEffect(() => {
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const mk = new mapboxgl.Marker({ element: el, anchor, offset: offset || [0, 0] }).setLngLat([lng, lat]).addTo(map);
    markerRef.current = mk;
    return () => {
      mk.remove();
      markerRef.current = null;
    };
     
  }, [map, el, anchor]);

  useEffect(() => {
    const mk = markerRef.current;
    if (!mk) return;
    if (Number.isFinite(lat) && Number.isFinite(lng)) mk.setLngLat([lng, lat]);
    mk.setOffset(offset || [0, 0]);
     
  }, [lat, lng, offset?.[0], offset?.[1]]);

  // never overwrite className wholesale: Mapbox owns `mapboxgl-marker` (absolute positioning) on this element
  const prevClass = useRef("");
  useEffect(() => {
    el.style.zIndex = zIndex != null ? String(zIndex) : "";
    if (prevClass.current) prevClass.current.split(/\s+/).filter(Boolean).forEach((c) => el.classList.remove(c));
    (className || "").split(/\s+/).filter(Boolean).forEach((c) => el.classList.add(c));
    prevClass.current = className || "";
  }, [el, zIndex, className]);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return createPortal(children, el);
};

/**
 * Transport legs as thin GeoJSON arcs. Planned legs are dashed and quiet; legs «på vei» get a soft glow and a slowly
 * travelling dash so the eye reads motion without anything flashing. Re-added after every basemap switch.
 */
export const TransportLines = ({ transports = [], selectedId }) => {
  const { map, ready, styleTick } = useOpsMap();
  const raf = useRef(null);
  const features = useMemo(
    () =>
      transports
        .filter((t) => t.from?.lat != null && t.to?.lat != null)
        .map((t) => ({
          type: "Feature",
          properties: { id: t.id, kind: t.in_transit ? "transit" : "planned", selected: t.id === selectedId || t.project_id === selectedId ? 1 : 0 },
          geometry: { type: "LineString", coordinates: arc(t.from, t.to) },
        })),
    [transports, selectedId]
  );

  useEffect(() => {
    if (!map || !ready) return;
    const data = { type: "FeatureCollection", features };
    if (!map.getSource("transports")) {
      map.addSource("transports", { type: "geojson", data, lineMetrics: true });
      // planned: a whisper-thin dashed line that firms up when its project is selected
      map.addLayer({ id: "transports-planned", type: "line", source: "transports", filter: ["==", ["get", "kind"], "planned"], layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#5b6b84", "line-width": ["case", ["==", ["get", "selected"], 1], 1.8, 1.1], "line-opacity": ["case", ["==", ["get", "selected"], 1], 0.85, 0.42], "line-dasharray": [0.6, 2.2] } });
      // in transit: soft glow + a gradient stroke from depot (quiet) to site (brand blue) + travelling dash
      map.addLayer({ id: "transports-transit-glow", type: "line", source: "transports", filter: ["==", ["get", "kind"], "transit"], layout: { "line-cap": "round" }, paint: { "line-color": "#2670F2", "line-width": 10, "line-opacity": 0.08, "line-blur": 5 } });
      map.addLayer({ id: "transports-transit", type: "line", source: "transports", filter: ["==", ["get", "kind"], "transit"], layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-width": 1.8, "line-opacity": 0.9, "line-gradient": ["interpolate", ["linear"], ["line-progress"], 0, "rgba(38,112,242,0.35)", 1, "rgba(38,112,242,1)"] } });
      map.addLayer({ id: "transports-transit-dash", type: "line", source: "transports", filter: ["==", ["get", "kind"], "transit"], layout: { "line-cap": "round" }, paint: { "line-color": "#ffffff", "line-width": 1.6, "line-opacity": 0.85, "line-dasharray": [0, 4, 3] } });
    } else {
      map.getSource("transports").setData(data);
    }
  }, [map, ready, styleTick, features]);

  // travelling dash (~12 fps, only while something is in transit)
  useEffect(() => {
    if (!map || !ready) return;
    const anyTransit = features.some((f) => f.properties.kind === "transit");
    if (!anyTransit) return;
    const seq = [
      [0, 4, 3], [0.5, 4, 2.5], [1, 4, 2], [1.5, 4, 1.5], [2, 4, 1], [2.5, 4, 0.5], [3, 4, 0], [0, 0.5, 3, 3.5], [0, 1, 3, 3], [0, 1.5, 3, 2.5], [0, 2, 3, 2], [0, 2.5, 3, 1.5], [0, 3, 3, 1], [0, 3.5, 3, 0.5],
    ];
    let step = 0, last = 0;
    const loop = (ts) => {
      if (ts - last > 90) {
        last = ts;
        step = (step + 1) % seq.length;
        if (map.getLayer("transports-transit-dash")) map.setPaintProperty("transports-transit-dash", "line-dasharray", seq[step]);
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [map, ready, styleTick, features]);

  return null;
};

/** Screen-space declutter hook: recomputes pixel offsets whenever the camera settles. */
export const useMarkerLayout = (points, radiusPx = 30) => {
  const { map, tick, zoom } = useOpsMap();
  return useMemo(() => {
    const project = map ? (p) => { const q = map.project([p.lng, p.lat]); return { x: q.x, y: q.y }; } : null;
    return layoutMarkers(points, project, radiusPx);
    // tick/zoom are deliberate dependencies: the camera moved, so screen distances changed
     
  }, [map, tick, zoom, points, radiusPx]);
};

/**
 * Geographic clustering hook. Below `clusterBelowZoom`, points that overlap on screen collapse into one cluster
 * (count + members); above it, they are decluttered with small offsets instead. Returns { clusters, singles, layout }.
 */
export const useClusters = (points, { radiusPx = 44, clusterBelowZoom = 9 } = {}) => {
  const { map, tick, zoom } = useOpsMap();
  return useMemo(() => {
    const project = map ? (p) => { const q = map.project([p.lng, p.lat]); return { x: q.x, y: q.y }; } : null;
    if (!project || zoom >= clusterBelowZoom) {
      return { clusters: [], singles: points, layout: layoutMarkers(points, project, 34) };
    }
    const groups = clusterPoints(points, project, radiusPx);
    const clusters = groups.filter((g) => g.items.length > 1);
    const singles = groups.filter((g) => g.items.length === 1).map((g) => g.items[0]);
    return { clusters, singles, layout: layoutMarkers(singles, project, 34) };
     
  }, [map, tick, zoom, points, radiusPx, clusterBelowZoom]);
};

/** Fit the camera to a set of points (used by cluster markers). */
export const useFitTo = () => {
  const { map } = useOpsMap();
  return (items, padding = 80) => {
    if (!map || !items?.length) return;
    const lngs = items.map((p) => p.lng), lats = items.map((p) => p.lat);
    const b = [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]];
    const same = b[0][0] === b[1][0] && b[0][1] === b[1][1];
    if (same) map.easeTo({ center: [b[0][0], b[0][1]], zoom: Math.min(map.getZoom() + 2.5, 13), duration: 600 });
    else map.fitBounds(b, { padding, maxZoom: 13, duration: 700 });
  };
};
